import { SEOHead } from "@/components/SEOHead";
import { Layout } from "@/components/layout/Layout";
import { Link } from "react-router-dom";
import { CheckCircle, ArrowRight, Clock, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { LucideIcon } from "lucide-react";

interface AutomatePageProps {
  seo: {
    title: string;
    description: string;
    keywords: string;
    canonicalUrl: string;
    noindex?: boolean;
  };
  badge: string;
  h1: React.ReactNode;
  subtitle: string;
  problemTitle: string;
  problems: { icon: LucideIcon; title: string; desc: string }[];
  solutionTitle: string;
  solutions: { title: string; desc: string }[];
  beforeItems: string[];
  afterItems: string[];
  relatedPages: { title: string; link: string; desc: string }[];
  ctaTitle: string;
  ctaDesc: string;
  faqItems: { q: string; a: string }[];
}

export const AutomatePage = ({ seo, badge, h1, subtitle, problemTitle, problems, solutionTitle, solutions, beforeItems, afterItems, relatedPages, ctaTitle, ctaDesc, faqItems }: AutomatePageProps) => {
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": `NoQ5 — ${seo.title.split('—')[0].trim()}`,
      "applicationCategory": "BusinessApplication",
      "description": seo.description,
      "url": seo.canonicalUrl,
      "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" },
      "publisher": { "@type": "Organization", "name": "NoQ5", "url": "https://noq5.ai" }
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": faqItems.map(f => ({
        "@type": "Question",
        "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a }
      }))
    }
  ];

  return (
    <Layout>
      <SEOHead {...seo} jsonLd={jsonLd} />

      <section className="pt-24 pb-16 bg-background">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="mb-6 text-sm text-muted-foreground">
            <Link to="/crm-agent" className="text-primary hover:underline">AI CRM Agent</Link>
            <span className="mx-2">→</span>
            <span>{badge}</span>
          </div>
          <div className="text-center">
            <span className="inline-block px-4 py-2 bg-muted text-foreground rounded-sm text-sm font-semibold mb-6">{badge}</span>
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6 leading-tight">{h1}</h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-8">{subtitle}</p>
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground" asChild>
              <Link to="/?scrollTo=demo">Book a Demo <ArrowRight className="ml-2 h-5 w-5" /></Link>
            </Button>
          </div>
        </div>
      </section>

      <section className="py-16 bg-destructive/5">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">{problemTitle}</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {problems.map((item, i) => (
              <div key={i} className="bg-card p-6 rounded-sm border border-border">
                <item.icon className="h-10 w-10 text-destructive mb-4" />
                <h3 className="text-lg font-bold mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">{solutionTitle}</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {solutions.map((item, i) => (
              <div key={i} className="bg-card p-6 rounded-sm border border-border">
                <h3 className="font-bold text-lg mb-2">{item.title}</h3>
                <p className="text-muted-foreground text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">Before vs. After</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-card p-8 rounded-sm border-2 border-destructive/30">
              <h3 className="text-xl font-bold text-destructive mb-6">❌ Manual Process</h3>
              <ul className="space-y-3">
                {beforeItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-muted-foreground"><span className="text-destructive mt-1">✗</span>{item}</li>
                ))}
              </ul>
            </div>
            <div className="bg-card p-8 rounded-sm border-2 border-primary/30">
              <h3 className="text-xl font-bold text-primary mb-6">✅ With NoQ5</h3>
              <ul className="space-y-3">
                {afterItems.map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-muted-foreground"><CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4 max-w-5xl">
          <h2 className="text-3xl font-bold text-center mb-12">Related Automations</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {relatedPages.map((item, i) => (
              <Link key={i} to={item.link} className="bg-card p-6 rounded-sm border border-border hover:border-primary/50 transition-colors group">
                <h3 className="font-bold text-lg mb-2 group-hover:text-primary">{item.title}</h3>
                <p className="text-muted-foreground text-sm mb-3">{item.desc}</p>
                <span className="text-primary text-sm font-medium flex items-center gap-1">Learn more <ArrowRight className="h-4 w-4" /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4 max-w-3xl">
          <h2 className="text-3xl font-bold text-center mb-12">FAQ</h2>
          <div className="space-y-6">
            {faqItems.map((item, i) => (
              <div key={i} className="bg-card p-6 rounded-sm border border-border">
                <h3 className="font-bold text-lg mb-2">{item.q}</h3>
                <p className="text-muted-foreground">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center max-w-3xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">{ctaTitle}</h2>
          <p className="text-xl opacity-90 mb-8">{ctaDesc}</p>
          <Button size="lg" variant="secondary" asChild>
            <Link to="/?scrollTo=demo">Book a Demo <ArrowRight className="ml-2 h-5 w-5" /></Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
};
